import { adjustmentValueOriginsSchema, adjustmentValuesSchema, dateStringValidation, estimateConfidenceSchema, estimateParametersSchema, estimateSourceSchema, isValidTimeZone, noticeEmailDeliverySchema, ownershipProfileTypeSchema, propertyRentProfileSchema, rentCapRuleSchema, rentIncreaseModeSchema, rentNoticeServedMethodSchema, rentNoticeStatusSchema, unitRentProfileSchema, } from '@teo/types';
import { z } from 'zod';
import { clearable } from './clearable.js';
import { displayId } from './common.js';
const timeZoneSchema = z.string().refine(isValidTimeZone, { message: 'Unknown time zone' });
const moneyInputSchema = z.number().min(0).max(1_000_000).multipleOf(0.01);
export const increaseSettingSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('percent'), value: z.number().min(0).max(100).multipleOf(0.01) }),
    z.object({ mode: z.literal('amount'), value: z.number().min(0).max(10_000).multipleOf(0.01) }),
]);
const increaseOutputSchema = z.object({ mode: rentIncreaseModeSchema, value: z.number() });
const vsMarketSchema = z.discriminatedUnion('band', [
    z.object({ band: z.literal('at') }),
    z.object({ band: z.literal('below'), gap: z.number() }),
    z.object({ band: z.literal('over'), gap: z.number() }),
]);
const computedRowSchema = z.object({
    delta: z.number(),
    newRent: z.number(),
    vsMarket: vsMarketSchema.nullable(),
    overCap: z.boolean(),
});
const marketOutputSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('estimate'),
        estimate: z
            .number()
            .describe("This unit's market rent: the AI figure for its unit type, plus the unit's condition, features and owner-paid utilities. When the AI figure does not price a plain unit, this is the AI figure."),
        low: z.number(),
        high: z.number(),
        estimatedAt: z.date(),
        grounded: z.boolean(),
        confidence: estimateConfidenceSchema,
    }),
    z.object({ kind: z.literal('override'), amount: z.number() }),
]);
export const rentNoticeOutputSchema = z.object({
    noticeId: z.string(),
    status: rentNoticeStatusSchema,
    tenantNames: z.array(z.string()),
    currentRent: z.number(),
    newRent: z.number(),
    effectiveDate: z.string(),
    noticeLeadDays: z.number(),
    preparedAt: z.date(),
    servedAt: z.date().nullable(),
    servedTimeZone: z.string().nullable(),
    servedMethods: z.array(rentNoticeServedMethodSchema),
    emailDelivery: noticeEmailDeliverySchema.nullable(),
    hasDocument: z.boolean(),
});
export const estimateNoteOutputSchema = z.object({
    text: z.string(),
    savedAt: z.date(),
    savedByName: z.string().nullable(),
});
const radarUnitFields = {
    unitId: z.string(),
    unitIdentifier: z.string().nullable(),
    unitTypeKey: z.string(),
    unitTypeLabel: z.string(),
    plan: z.object({
        increase: increaseOutputSchema.nullable(),
        marketOverride: z.number().nullable(),
        lastRaiseDate: z.string().nullable(),
        unitProfile: unitRentProfileSchema,
        estimateNote: estimateNoteOutputSchema.optional(),
        inspectedDate: dateStringValidation.optional(),
    }),
    market: marketOutputSchema.nullable(),
    latestNotice: rentNoticeOutputSchema.nullable(),
};
const occupiedFields = {
    lease: z.object({
        leaseId: z.string(),
        startDate: z.string(),
        endDate: z.string().nullable(),
        currentRent: z.number(),
    }),
    tenantNames: z.array(z.string()),
    increase: increaseOutputSchema.extend({ source: z.enum(['unit', 'property']) }),
    computed: computedRowSchema,
    openBalance: z.number().nullable(),
};
export const radarRowSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('eligible'), ...radarUnitFields, ...occupiedFields }),
    z.object({ status: z.literal('upcoming'), ...radarUnitFields, ...occupiedFields }),
    z.object({ status: z.literal('vacant'), ...radarUnitFields }),
]);
export const radarSummarySchema = z.object({
    readyCount: z.number(),
    upcomingCount: z.number(),
    monthlyUplift: z.number(),
    annualUplift: z.number(),
    belowMarketCount: z.number(),
    unknownLastRaiseCount: z.number(),
    monthlyLeftOnTable: z.number(),
    annualLeftOnTable: z.number(),
    unitsBelowMarket: z.number(),
    unitsWithoutMarket: z.number(),
});
const settingsFields = {
    increaseMode: rentIncreaseModeSchema,
    increaseValue: z.number(),
    noticeLeadDays: z.number(),
    maxIncreasePct: z.number(),
    estimateParameters: estimateParametersSchema,
    propertyProfile: propertyRentProfileSchema,
    adjustmentValues: adjustmentValuesSchema,
    adjustmentValueOrigins: adjustmentValueOriginsSchema,
    estimateNote: estimateNoteOutputSchema.optional(),
    capRule: rentCapRuleSchema,
    cpiPercent: z.number().nullable(),
    cpiLabel: z.string().nullable(),
    ab1482Exempt: z.boolean(),
    effectiveCapPct: z.number().nullable(),
};
const radarAsOf = dateStringValidation.describe('The date to read the radar on, as YYYY-MM-DD. Use today unless asked about another date.');
export const getRentRadarSchema = z.object({
    propertyId: displayId,
    asOf: radarAsOf,
});
export const getRentRadarOutputSchema = z.object({
    property: z.object({
        propertyId: z.string(),
        label: z.string(),
        ownershipName: z.string(),
        ownershipProfileType: ownershipProfileTypeSchema,
    }),
    asOf: z.string(),
    canEdit: z.boolean(),
    effectiveDate: z.string(),
    settings: z.object({ ...settingsFields, saved: z.boolean() }),
    unitTypes: z.array(z.object({
        key: z.string(),
        label: z.string(),
        unitCount: z.number(),
        estimate: z
            .object({
            estimate: z
                .number()
                .describe("The AI figure for this unit type, before each unit's condition, features and owner-paid utilities. The market rent of one unit is in rows[].market."),
            low: z.number(),
            high: z.number(),
            rationale: z.string(),
            jurisdictionNote: z.string(),
            estimatedAt: z.date(),
            pricesPlainUnit: z
                .boolean()
                .describe('True when the AI figure prices a plain unit, so each unit adds its condition, features and owner-paid utilities. False when the figure came from the first prompt, which priced the condition and the inside amenities into it: each unit then reads the figure with nothing added until the estimate is refreshed.'),
            grounded: z.boolean(),
            confidence: estimateConfidenceSchema,
            sources: z.array(estimateSourceSchema),
            searchEntryPoint: z.string().optional(),
        })
            .nullable(),
    })),
    rows: z.array(radarRowSchema),
    summary: radarSummarySchema,
});
export const saveRentRadarSettingsSchema = z.object({
    propertyId: displayId,
    increase: increaseSettingSchema,
    noticeLeadDays: z.number().int().min(0).max(365),
    maxIncreasePct: z.number().min(0).max(100).multipleOf(0.01),
    estimateParameters: estimateParametersSchema,
    propertyProfile: propertyRentProfileSchema,
    capRule: rentCapRuleSchema,
    cpiPercent: z.number().min(0).max(100).multipleOf(0.01).nullable(),
    cpiLabel: z.string().max(120).nullable(),
    ab1482Exempt: z.boolean(),
});
export const voidedNoticesOutputSchema = z.object({ voidedNoticeCount: z.number() });
export const saveUnitRentPlanSchema = z.object({
    unitId: displayId,
    increase: clearable(increaseSettingSchema),
    marketOverride: clearable(moneyInputSchema),
    lastRaiseDate: clearable(dateStringValidation),
});
export const refreshRentEstimateSchema = z.object({ propertyId: displayId });
export const refreshRentEstimateOutputSchema = z.object({
    unitTypeCount: z.number(),
    jurisdictionNote: z.string(),
    missingInputs: z.array(z.string()),
});
export const rentRadarSummaryInputSchema = z.object({
    asOf: radarAsOf,
    ownershipId: displayId,
});
export const rentRadarSummaryOutputSchema = z.object({
    properties: z.array(radarSummarySchema.extend({ propertyId: z.string(), label: z.string() })),
    totals: radarSummarySchema,
});
export const RENT_NOTICE_PRINT_CAP = 100;
export const RENT_NOTICE_PREPARE_CAP = 50;
export const prepareRentNoticesSchema = z.object({
    propertyId: displayId,
    unitIds: z.array(displayId).min(1).max(RENT_NOTICE_PREPARE_CAP),
    timeZone: timeZoneSchema,
});
export const prepareRentNoticesOutputSchema = z.object({
    results: z.array(z.discriminatedUnion('outcome', [
        z.object({
            unitId: z.string(),
            outcome: z.literal('prepared'),
            noticeId: z.string(),
            effectiveDate: z.string(),
        }),
        z.object({ unitId: z.string(), outcome: z.literal('not_found') }),
        z.object({ unitId: z.string(), outcome: z.literal('vacant') }),
        z.object({ unitId: z.string(), outcome: z.literal('upcoming') }),
        z.object({ unitId: z.string(), outcome: z.literal('no_tenant') }),
        z.object({ unitId: z.string(), outcome: z.literal('refused'), message: z.string() }),
    ])),
    voidedNoticeCount: z.number(),
});
export const markRentNoticeServedSchema = z.object({
    noticeId: displayId,
    methods: z.array(rentNoticeServedMethodSchema).min(1).max(3),
    servedAt: z.date(),
    timeZone: timeZoneSchema,
});
export const emailRentNoticeSchema = z.object({
    noticeId: displayId,
    timeZone: timeZoneSchema,
});
export const emailRentNoticeOutputSchema = z.object({ emailDelivery: noticeEmailDeliverySchema });
export const printRentNoticesSchema = z.object({
    propertyId: displayId,
    noticeIds: z.array(displayId).min(1).max(RENT_NOTICE_PRINT_CAP),
});
//# sourceMappingURL=rentRadar.js.map